import React, { useState, useMemo, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import DatePicker, { registerLocale } from 'react-datepicker';
import sv from 'date-fns/locale/sv';
import { ArrowLeftIcon, ArrowRightIcon } from '../assets/dashboard-icon.svg';
import '../scss/pre-insurance-dashboard.style.scss';
// Imported only for the .mi-hero class used by the new heading. Every other
// rule in this file is scoped under .manage-invoice-v2 and can't leak here.
import '../scss/components/manage-invoice.style.scss';
import { fetchUserInsurance, UserInsuranceRecord } from '../api/user-insurance';
import { getInvoiceEditDataApi, Customer } from '../features/customer/customer.service';
import AddCustomerModal from './add-customer-modal';
import {
  TableRow,
  TableCell,
  TableHeader,
  TableColumn,
  MobileCardRow,
  MobileCardWrapper,
  TableBody,
} from '../../v2/components/dashboard/table-components';
import './../scss/components/invoice-table.style.scss';
import { createUserInsurance, resetUserInsuranceState } from '../features/user-insurance/user-insurance.slice';
import { AppDispatch, RootState } from '../store';
import GenericModal from './modal/generic-modal.component';

/* Register the Swedish locale with react-datepicker once at module
   load — `locale="sv"` then swaps month names, weekday headers
   and screen-reader strings to Swedish. */
registerLocale('sv', sv);

/* Portal the calendar popper to <body> so it escapes the form
   card's overflow / transform / rounded corners and is never
   clipped. Paired with `popperProps.strategy: 'fixed'` for
   viewport-relative positioning. */
const PreInsurancePopperPortal: React.FC<{ children: React.ReactNode }> = ({ children }) =>
  ReactDOM.createPortal(children, document.body);

/* String <-> Date bridges. `workStartDate` stays a yyyy-mm-dd
   string everywhere outside the picker (createUserInsurance's
   coverageStart payload, validation, button-disabled checks), so
   no upstream contract changes. */
const parseIso = (s?: string): Date | null => {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};
const toIso = (d: Date | null): string => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/* Render the title with the LAST word styled as a violet gradient brand
   accent — works for any locale. Single-word titles are fully styled. */
const renderHeroTitle = (title: string) => {
    const trimmed = (title || '').trim();
    if (!trimmed) return null;
    const idx = trimmed.lastIndexOf(' ');
    if (idx === -1) {
        return <span className="mi-hero__brand">{trimmed}</span>;
    }
    const prefix = trimmed.slice(0, idx);
    const last = trimmed.slice(idx + 1);
    return (
        <>
            {prefix}{' '}
            <span className="mi-hero__brand">{last}</span>
        </>
    );
};

const CalendarIcon: React.FC = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="4.5" width="18" height="17" rx="2.5" stroke="currentColor" strokeWidth="2" />
    <path d="M3 9h18M8 2.5v4M16 2.5v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export const PreInsuranceDashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  /* Re-evaluates whenever i18next switches language; the picker
     re-renders with Swedish month/weekday labels in sv-se and
     falls back to library default (English) otherwise. */
  const calendarLocale = (i18n.language || '').toLowerCase().startsWith('sv') ? 'sv' : undefined;
  const isMobile = useMediaQuery('(max-width: 768px)');
  const dispatch = useDispatch<AppDispatch>();

  const [selectedClient, setSelectedClient] = useState('');
  const [workStartDate, setWorkStartDate] = useState('');
  const [description, setDescription] = useState('');
  const [agreedToPolicy, setAgreedToPolicy] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const itemsPerPage = 10;

  // API state
  const [assignments, setAssignments] = useState<UserInsuranceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  // Redux state for form submission
  const submitLoading = useSelector((state: RootState) => state.userInsurance?.loading ?? false);
  const submitSuccess = useSelector((state: RootState) => state.userInsurance?.submitSuccess ?? false);

  // Customer state
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);

  // Customer search/dropdown state
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const customerDropdownRef = useRef<HTMLDivElement>(null);
  const customerSearchInputRef = useRef<HTMLInputElement>(null);

  // Add Customer Modal state
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  
  // Insurance Letter PDF Modal state
  const [showPdfModal, setShowPdfModal] = useState(false);

  // Ref to track latest customers for polling
  const latestCustomersRef = useRef<Customer[]>([]);

  // Load customers function
  const loadCustomers = async () => {
    setCustomersLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = userData?.userData?.userId;

      if (userId) {
        const response = await getInvoiceEditDataApi(userId);
        if (response.statusCode === 200 && response.response?.customers) {
          setCustomers(response.response.customers);
          latestCustomersRef.current = response.response.customers;
        }
      }
    } catch (err) {
      console.error('Error fetching customers:', err);
    } finally {
      setCustomersLoading(false);
    }
  };

  const handleDownloadLetter = () => {
    setShowPdfModal(true);
  };
  
  const handleDownloadPdf = () => {
    const link = document.createElement('a');
    link.href = 'https://api-prod.invozio.com/img/F%C3%B6rs%C3%A4kringsbrev.pdf';
    link.target = '_blank';
    link.download = 'Försäkringsbrev.pdf';
    link.click();
  };

  // Fetch customers on mount
  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    const loadUserInsurance = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchUserInsurance({
          PageNumber: currentPage,
          PageSize: itemsPerPage,
        });

        if (response.statusCode === 200) {
          setAssignments(response.response);
          setTotalCount(response.totalCount);
        } else {
          setError(response.message || 'Failed to load insurance data');
        }
      } catch (err: any) {
        console.error('Error fetching user insurance:', err);
        setError(err?.response?.data?.message || 'An error occurred while loading data');
      } finally {
        setLoading(false);
      }
    };

    loadUserInsurance();
  }, [currentPage, reloadTrigger]);

  // Close customer dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target as Node)) {
        setIsCustomerDropdownOpen(false);
        setCustomerSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isCustomerDropdownOpen && customerSearchInputRef.current) {
      setTimeout(() => {
        customerSearchInputRef.current?.focus();
        customerSearchInputRef.current?.select();
      }, 100);
    } else if (!isCustomerDropdownOpen) {
      setCustomerSearchQuery('');
    }
  }, [isCustomerDropdownOpen]);

  // Filter customers based on search query
  const filteredCustomers = useMemo(() => {
    if (!customerSearchQuery.trim()) {
      return customers;
    }
    const searchLower = customerSearchQuery.toLowerCase();
    return customers.filter(customer =>
      customer.name?.toLowerCase().includes(searchLower) ||
      customer.email?.toLowerCase().includes(searchLower) ||
      customer.orgNo?.toLowerCase().includes(searchLower)
    );
  }, [customers, customerSearchQuery]);

  // Get selected customer name for display
  const selectedCustomerName = useMemo(() => {
    if (!selectedClient) return '';
    const customer = customers.find(c => String(c.customerId || c.id) === String(selectedClient));
    return customer?.name || '';
  }, [selectedClient, customers]);

  // Handle customer selection
  const handleCustomerSelect = (customerId: number | string) => {
    setSelectedClient(String(customerId));
    setIsCustomerDropdownOpen(false);
    setCustomerSearchQuery('');
  };

  // Avatar helpers — mirror the Manage Invoice client cell (initials + tinted variant)
  const getClientInitial = (name?: string) => {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    return parts.slice(0, 2).map((p) => p.charAt(0)).join('').toUpperCase();
  };

  const getAvatarVariant = (name?: string) => {
    const key = (name || '').trim();
    let sum = 0;
    for (let i = 0; i < key.length; i++) sum += key.charCodeAt(i);
    return (sum % 5) + 1;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    /* Single canonical display format across both locales — every
       date in this view (coverage start, created date, etc) renders
       as yyyy-MM-dd. The English branch used to emit "15 Apr 2026"
       which broke at-a-glance consistency with the picker input
       below and with the rest of the Holiday / Pensions reports. */
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Calculate pagination based on API totalCount
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const paginatedAssignments = useMemo(() => {
    return assignments;
  }, [assignments]);

  // Pagination helpers
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (
        let i = Math.max(2, currentPage - 1);
        i <= Math.min(totalPages - 1, currentPage + 1);
        i++
      ) {
        if (!pages.includes(i)) pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!selectedClient || !workStartDate || !description || !agreedToPolicy) {
      return;
    }

    // Disable button immediately
    setIsSubmitting(true);

    try {
      const result = await dispatch(createUserInsurance({
        data: {
          coverageStart: workStartDate,
          customerId: parseInt(selectedClient),
          typeOfWork: description,
          insuranceConfirmation: agreedToPolicy,
        },
        language: i18n.language,
      }));

      // Check if the action was fulfilled
      if (createUserInsurance.fulfilled.match(result)) {
        // Reset form
        setSelectedClient('');
        setWorkStartDate('');
        setDescription('');
        setAgreedToPolicy(false);
        setCustomerSearchQuery('');

        // Reload customers
        await loadCustomers();

        // Reload assignments table
        setCurrentPage(1);
        setReloadTrigger(prev => prev + 1);

        // Reset Redux state
        dispatch(resetUserInsuranceState());
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      // Re-enable button after submission completes
      setIsSubmitting(false);
    }
  };

  const showFrom = totalCount === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const showTo = Math.min(currentPage * itemsPerPage, totalCount);

  return (
    <>
      <div className="pre-insurance-v2">
        {/* Hero */}
        {/* Hero header — same soft violet card used by the other dashboard
            pages. The shield SVG is preserved inside the badge as a thematic
            touch for the (pre-)insurance context. */}
        <div className="mi-hero">
          <span className="mi-hero__badge">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <path d="M12 2 4 5v6c0 5 3.4 8.5 8 11 4.6-2.5 8-6 8-11V5l-8-3z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
              <path d="m9 12 2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {t('PI_HERO_BADGE')}
          </span>
          <h1 className="mi-hero__title">
            {renderHeroTitle(t('PRE_INSURANCE'))}
          </h1>
          <p className="mi-hero__subtitle">{t('PRE_INSURANCE_DASHBOARD_DESC')}</p>
        </div>

        {/* Purpose explainer — tells users what Assignment Registration is for
            and when to use it (client feedback). Purely informational; does not
            touch any form/table logic. */}
        <div className="pi-info">
          <span className="pi-info__blob" aria-hidden="true" />
          <div className="pi-info__icon" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M9 18h6M10 21h4M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.2 1 2v.3h6v-.3c0-.8.4-1.5 1-2A7 7 0 0 0 12 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="pi-info__content">
            <span className="pi-info__eyebrow">{t('PI_INFO_EYEBROW')}</span>
            <h2 className="pi-info__title">{t('PI_INFO_TITLE')}</h2>
            <p className="pi-info__lead">{t('PI_INFO_LEAD')}</p>
            <ul className="pi-info__list">
              {[
                {
                  key: 'PI_INFO_POINT_1',
                  icon: (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                      <path d="M14 3v5h5M8 13h8M8 17h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ),
                },
                {
                  key: 'PI_INFO_POINT_2',
                  icon: (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2 4 5v6c0 5 3.4 8.5 8 11 4.6-2.5 8-6 8-11V5l-8-3z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                      <path d="m9 12 2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ),
                },
                {
                  key: 'PI_INFO_POINT_3',
                  icon: (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                      <circle cx="12" cy="12" r="3.5" stroke="currentColor" strokeWidth="2" />
                      <path d="m5 5 4 4M15 15l4 4M19 5l-4 4M9 15l-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  ),
                },
                {
                  key: 'PI_INFO_POINT_4',
                  icon: (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                      <path d="M3 9.5 12 4l9 5.5M5 10v8m14-8v8M9 10v8m6-8v8M3 21h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ),
                },
              ].map((p, i) => (
                <li className="pi-info__item" key={p.key}>
                  <span className={`pi-info__pt-icon pi-info__pt-icon--v${i + 1}`} aria-hidden="true">
                    {p.icon}
                  </span>
                  <span className="pi-info__pt-text">{t(p.key)}</span>
                </li>
              ))}
            </ul>
            <div className="pi-info__chips">
              <span className="pi-info__chips-label">{t('PI_INFO_PARTNERS')}</span>
              <span className="pi-info__chip">Trygg-Hansa</span>
              <span className="pi-info__chip">Skatteverket</span>
              <span className="pi-info__chip">Försäkringskassan</span>
            </div>
          </div>
        </div>

        <div className="pi-grid">
          {/* All Assignments Card */}
          <div className="pi-card pi-card--table">
            <div className="pi-card__header">
              <h2 className="pi-card__title">
                {t('ALL_ASSIGNMENTS')}
                {!loading && !error && totalCount > 0 && (
                  <span className="pi-count-badge">{totalCount}</span>
                )}
              </h2>
            </div>
            <div className="pi-card__body">
              <div className="invoice-table-container">
                {/* Loading State — skeleton shimmer */}
                {loading && (
                  <div className="pi-skeleton" role="status" aria-label={t('LOADING')}>
                    {!isMobile && (
                      <div className="pi-skeleton__head">
                        <span>{t('CLIENT_NAME')}</span>
                        <span>{t('WORK_START_DATE')}</span>
                        <span>{t('REQUESTED_DATE')}</span>
                      </div>
                    )}
                    {Array.from({ length: 6 }).map((_, r) => (
                      <div className="pi-skeleton__row" key={`sk-${r}`}>
                        <div className="pi-skeleton__cell pi-skeleton__cell--client">
                          <span className="pi-skeleton__avatar" />
                          <span className="pi-skeleton__line" />
                        </div>
                        <div className="pi-skeleton__cell"><span className="pi-skeleton__line" /></div>
                        <div className="pi-skeleton__cell"><span className="pi-skeleton__line" /></div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Error State */}
                {error && !loading && (
                  <div className="pi-error">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                      <path d="M12 8v5M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <p>{error}</p>
                  </div>
                )}

                {/* Empty State — illustrated violet */}
                {!loading && !error && paginatedAssignments.length === 0 && (
                  <div className="pi-empty">
                    <div className="pi-empty__icon" aria-hidden="true">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                        <path d="M9 2h6a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6l4-4z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                        <path d="M9 2v4h4" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                        <path d="M9 14h6M9 18h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </div>
                    <h4 className="pi-empty__title">{t('NO_ASSIGNMENTS_FOUND')}</h4>
                    <p className="pi-empty__message">{t('NO_ASSIGNMENTS_HINT')}</p>
                  </div>
                )}

                {/* Data Table */}
                {!loading && !error && paginatedAssignments.length > 0 && (
                  <div className="invoice-table-content">
                    <div className="invoice-table-scroll-wrapper">
                      <div className="invoice-table">
                        {!isMobile && (
                          <TableHeader>
                            <TableColumn>{t('CLIENT_NAME')}</TableColumn>
                            <TableColumn>{t('WORK_START_DATE')}</TableColumn>
                            <TableColumn>{t('REQUESTED_DATE')}</TableColumn>
                          </TableHeader>
                        )}

                        <TableBody>
                          {isMobile ? (
                            // Mobile: Card-based layout
                            paginatedAssignments.map((assignment) => (
                              <MobileCardWrapper key={assignment.id}>
                                <MobileCardRow label={t('CLIENT_NAME')}>
                                  <span className="mi-client">
                                    <span className={`mi-client__avatar mi-client__avatar--v${getAvatarVariant(assignment.customer?.name)}`}>
                                      {getClientInitial(assignment.customer?.name)}
                                    </span>
                                    <span className="mi-client__info">
                                      <span className="mi-client__name">{assignment.customer?.name || '-'}</span>
                                    </span>
                                  </span>
                                </MobileCardRow>
                                <MobileCardRow label={t('WORK_START_DATE')}>
                                  <span className="pi-date"><CalendarIcon /> {formatDate(assignment.coverageStart)}</span>
                                </MobileCardRow>
                                <MobileCardRow label={t('REQUESTED_DATE')}>
                                  <span className="pi-date"><CalendarIcon /> {formatDate(assignment.createdDate)}</span>
                                </MobileCardRow>
                              </MobileCardWrapper>
                            ))
                          ) : (
                            // Desktop: Traditional table row layout
                            paginatedAssignments.map((assignment) => (
                              <TableRow key={assignment.id}>
                                <TableCell>
                                  <span className="mi-client">
                                    <span className={`mi-client__avatar mi-client__avatar--v${getAvatarVariant(assignment.customer?.name)}`}>
                                      {getClientInitial(assignment.customer?.name)}
                                    </span>
                                    <span className="mi-client__info">
                                      <span className="mi-client__name">{assignment.customer?.name || '-'}</span>
                                    </span>
                                  </span>
                                </TableCell>
                                <TableCell>
                                  <span className="pi-date"><CalendarIcon /> {formatDate(assignment.coverageStart)}</span>
                                </TableCell>
                                <TableCell>
                                  <span className="pi-date"><CalendarIcon /> {formatDate(assignment.createdDate)}</span>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </div>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="invoice-table-footer">
                        <span className="invoice-table-footer__count">
                          {t('SHOWING_ASSIGNMENTS_COUNT', {
                            start: showFrom,
                            end: showTo,
                            total: totalCount,
                            defaultValue: 'Showing {{start}}–{{end}} of {{total}} assignments',
                          })}
                        </span>
                        <div className="invoice-table-pagination">
                          <div className="pagination-controls">
                            <button
                              className="pagination-button pagination-button--prev"
                              onClick={goToPreviousPage}
                              disabled={currentPage === 1}
                            >
                              <ArrowLeftIcon />
                              <span>{t('PREV_STEP')}</span>
                            </button>

                            {getPageNumbers().map((page, index) => {
                              if (page === '...') {
                                return (
                                  <button
                                    key={`ellipsis-${index}`}
                                    className="pagination-button pagination-button--number"
                                    disabled
                                  >
                                    ...
                                  </button>
                                );
                              }
                              return (
                                <button
                                  key={page}
                                  className={`pagination-button pagination-button--number${page === currentPage ? ' pagination-button--active' : ''
                                    }`}
                                  onClick={() => setCurrentPage(page as number)}
                                >
                                  {page}
                                </button>
                              );
                            })}

                            <button
                              className="pagination-button pagination-button--next"
                              onClick={goToNextPage}
                              disabled={currentPage === totalPages}
                            >
                              <span>{t('NEXT_STEP')}</span>
                              <ArrowRightIcon />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Register New Assignment Card */}
          <div className="pi-card pi-card--form">
            <div className="pi-card__header">
              <span className="pi-card__header-icon">
                <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                  <path d="M8 3.33333V12.6667M3.33333 8H12.6667" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <div className="pi-card__header-text">
                <h2 className="pi-card__title">{t('REGISTER_NEW_ASSIGNMENT').replace(/^\+\s*/, '')}</h2>
                <p className="pi-card__subtitle">{t('REGISTER_ASSIGNMENT_SUBTITLE')}</p>
              </div>
            </div>
            <div className="pi-card__body">
              <form onSubmit={handleSubmit} className="pre-insurance-form">
                {/* Client Field */}
                <div className="form-field">
                  <label className="form-label">
                    {t('CLIENT')} <span className="required">*</span>
                  </label>
                  <div className="input-wrapper" ref={customerDropdownRef}>
                    <div className="custom-dropdown">
                      <input
                        ref={customerSearchInputRef}
                        type="text"
                        className="form-input"
                        placeholder={customersLoading ? t('LOADING') : t('SEARCH_OR_SELECT_CLIENT')}
                        value={isCustomerDropdownOpen ? customerSearchQuery : selectedCustomerName}
                        onChange={(e) => {
                          setCustomerSearchQuery(e.target.value);
                          if (!isCustomerDropdownOpen) {
                            setIsCustomerDropdownOpen(true);
                          }
                        }}
                        onFocus={() => {
                          if (!isCustomerDropdownOpen) {
                            setIsCustomerDropdownOpen(true);
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        readOnly={!isCustomerDropdownOpen}
                        disabled={customersLoading}
                        required
                      />
                      <svg
                        className={`select-arrow ${isCustomerDropdownOpen ? 'rotated' : ''}`}
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!customersLoading) {
                            setIsCustomerDropdownOpen(!isCustomerDropdownOpen);
                          }
                        }}
                        style={{ cursor: customersLoading ? 'not-allowed' : 'pointer' }}
                      >
                        <path d="M5 7.5L10 12.5L15 7.5" stroke="#68727D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>

                      {isCustomerDropdownOpen && !customersLoading && (
                        <div className="dropdown-list">
                          <div className="dropdown-content">
                            {filteredCustomers.length > 0 ? (
                              filteredCustomers.map((customer) => (
                                <div
                                  key={customer.customerId || customer.id}
                                  className={`list-item ${String(selectedClient) === String(customer.customerId || customer.id) ? 'selected' : ''}`}
                                  onClick={() => handleCustomerSelect(customer.customerId || customer.id!)}
                                >
                                  <div className="customer-info">
                                    <span className="customer-name">{customer.name}</span>
                                    {customer.email && (
                                      <span className="customer-email">{customer.email}</span>
                                    )}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="list-item no-results">
                                {t('NO_RESULTS_FOUND')}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Add New Client Button */}
                <button type="button" className="add-client-btn" onClick={() => setShowAddCustomerModal(true)}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M8 3.33333V12.6667M3.33333 8H12.6667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {t('ADD_NEW_CLIENT')}
                </button>

                {/* Work Start Date Field */}
                <div className="form-field">
                  <label className="form-label">
                    {t('WORK_START_DATE')} <span className="required">*</span>
                  </label>
                  <div className="input-wrapper">
                    <DatePicker
                      selected={parseIso(workStartDate)}
                      onChange={(d: Date | null) => setWorkStartDate(toIso(d))}
                      dateFormat="yyyy-MM-dd"
                      placeholderText="yyyy-mm-dd"
                      className="form-input"
                      wrapperClassName="pre-insurance__date-input-dp"
                      popperContainer={PreInsurancePopperPortal}
                      popperProps={{ strategy: 'fixed' }}
                      popperClassName="pre-insurance__date-dp-popper"
                      calendarClassName="pre-insurance__date-dp-calendar"
                      onChangeRaw={(e: React.SyntheticEvent | undefined) => e?.preventDefault()}
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="scroll"
                      locale={calendarLocale}
                      autoComplete="off"
                      required
                    />
                    <svg className="input-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <rect x="3" y="4" width="14" height="14" rx="2" stroke="#68727D" strokeWidth="1.5" />
                      <path d="M3 8H17M7 2V4M13 2V4" stroke="#68727D" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </div>
                </div>

                {/* Description Field */}
                <div className="form-field">
                  <label className="form-label">{t('DESCRIPTION')}<span className="required">*</span></label>
                  <textarea
                    className="form-textarea"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={t('WRITE_ASSIGNMENT_DESCRIPTION')}
                    rows={6}
                    required
                  />
                </div>

                {/* Insurance Policy Checkbox */}
                <div className="form-checkbox-wrapper">
                  <input
                    type="checkbox"
                    id="insurance-policy"
                    checked={agreedToPolicy}
                    onChange={(e) => setAgreedToPolicy(e.target.checked)}
                    required
                  />
                  <label htmlFor="insurance-policy" className="checkbox-label">
                    {t('I_HAVE_READ_AGREE_TO')}
                    <span
                      className="policy-link"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowPdfModal(true);
                      }}
                      style={{ cursor: 'pointer', textDecoration: 'underline', color: '#0066cc' }}
                    >
                      {t('INSURANCE_POLICY')}
                    </span>
                    {t('INSURANCE_POLICY_SUFFIX')}
                  </label>
                </div>
              </form>
            </div>
            <div className="pi-card__footer">
              <button
                type="submit"
                className="submit-btn"
                onClick={handleSubmit}
                disabled={!agreedToPolicy || !selectedClient || !workStartDate || !description || submitLoading || isSubmitting}
              >
                {(submitLoading || isSubmitting) ? (
                  <>
                    <span className="submit-btn__spinner" />
                    {t('SUBMITTING')}
                  </>
                ) : t('SUBMIT')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Add Customer Modal */}
      <AddCustomerModal
        show={showAddCustomerModal}
        onHide={() => setShowAddCustomerModal(false)}
        onSave={(customerData) => {
          setShowAddCustomerModal(false);

          // Reload customers and auto-select the new customer
          const reloadAndSelectCustomer = async () => {
            try {
              const userData = JSON.parse(localStorage.getItem('user') || '{}');
              const userId = userData?.userData?.userId;

              if (userId) {
                // Extract customer identifiers from the form data
                const customerName = customerData?.customerName || '';
                const customerEmail = customerData?.contactEmail || '';
                const customerSSN = customerData?.ssn || '';

                // Polling mechanism to wait for customer to appear
                const maxRetries = 50; // Maximum 50 retries (25 seconds total)
                const retryInterval = 500; // Check every 500ms
                let retryCount = 0;

                const pollForNewCustomer = async () => {
                  retryCount++;

                  // Fetch latest customers
                  const response = await getInvoiceEditDataApi(userId);
                  if (response.statusCode === 200 && response.response?.customers) {
                    const currentCustomers = response.response.customers;
                    setCustomers(currentCustomers);
                    latestCustomersRef.current = currentCustomers;

                    // Try to find the newly created customer
                    const foundCustomer = currentCustomers.find((c: Customer) => {
                      const nameMatch = c.name?.toLowerCase() === customerName?.toLowerCase();
                      const emailMatch = c.email?.toLowerCase() === customerEmail?.toLowerCase();
                      const ssnMatch = customerSSN && c.ssn === customerSSN;
                      return nameMatch || emailMatch || ssnMatch;
                    });

                    if (foundCustomer) {
                      const customerId = (foundCustomer.customerId || foundCustomer.id)?.toString() || '';
                      setSelectedClient(customerId);
                      setCustomerSearchQuery('');
                      setIsCustomerDropdownOpen(false);
                    } else if (retryCount < maxRetries) {
                      // Customer not found yet, retry
                      setTimeout(pollForNewCustomer, retryInterval);
                    } else {
                      // Max retries reached, select last customer as fallback
                      if (currentCustomers.length > 0) {
                        const lastCustomer = currentCustomers[currentCustomers.length - 1];
                        const lastCustomerId = (lastCustomer.customerId || lastCustomer.id)?.toString() || '';
                        setSelectedClient(lastCustomerId);
                        setCustomerSearchQuery('');
                        setIsCustomerDropdownOpen(false);
                      }
                    }
                  }
                };

                // Start polling after 1 second delay
                setTimeout(pollForNewCustomer, 1000);
              }
            } catch (err) {
              console.error('Error reloading customers:', err);
            }
          };

          reloadAndSelectCustomer();
        }}
      />
      
      {/* Insurance Letter PDF Modal */}
      <GenericModal
        show={showPdfModal}
        onClose={() => setShowPdfModal(false)}
        title={i18n.language?.toLowerCase().includes('sv') ? 'Försäkringsbrev' : 'Insurance Letter'}
        size="xl"
        showFooter={false}
        variant="centered"
      >
        <div>
     <div className="d-flex justify-content-between align-items-center mb-3">
  <div>
    <span className="p-3">
    <svg
  width={24}
  height={24}
  viewBox="0 0 24 24"
  fill="none"
  xmlns="http://www.w3.org/2000/svg"
>
  <path
    d="M8 13C7.44772 13 7 13.4477 7 14C7 14.5523 7.44772 15 8 15V14V13ZM16 15C16.5523 15 17 14.5523 17 14C17 13.4477 16.5523 13 16 13V14V15ZM8 9C7.44772 9 7 9.44772 7 10C7 10.5523 7.44772 11 8 11V10V9ZM16 11C16.5523 11 17 10.5523 17 10C17 9.44772 16.5523 9 16 9V10V11ZM8 5C7.44772 5 7 5.44772 7 6C7 6.55228 7.44772 7 8 7V6V5ZM16 7C16.5523 7 17 6.55228 17 6C17 5.44772 16.5523 5 16 5V6V7ZM8 17C7.44772 17 7 17.4477 7 18C7 18.5523 7.44772 19 8 19V18V17ZM12 19C12.5523 19 13 18.5523 13 18C13 17.4477 12.5523 17 12 17V18V19ZM5.07805 21.7748L4.61708 22.6622L5.07805 21.7748ZM4.22517 20.9219L3.33776 21.3829L4.22517 20.9219ZM19.7748 20.9219L20.6622 21.3829L19.7748 20.9219ZM18.9219 21.7748L19.3829 22.6622L18.9219 21.7748ZM18.9219 2.22517L19.3829 1.33776L18.9219 2.22517ZM19.7748 3.07805L20.6622 2.61708L19.7748 3.07805ZM5.07805 2.22517L4.61708 1.33776L5.07805 2.22517ZM4.22517 3.07805L3.33776 2.61708L4.22517 3.07805ZM8 14V15H16V14V13H8V14ZM8 10V11H16V10V9H8V10ZM8 6V7H16V6V5H8V6ZM8 18V19H12V18V17H8V18ZM7.22 2V3H16.78V2V1H7.22V2ZM20 5.22H19V18.78H20H21V5.22H20ZM16.78 22V21H7.22V22V23H16.78V22ZM4 18.78H5V5.22H4H3V18.78H4ZM7.22 22V21C6.63372 21 6.25517 20.9992 5.96722 20.9753C5.69125 20.9524 5.58924 20.9135 5.53903 20.8874L5.07805 21.7748L4.61708 22.6622C5.00034 22.8613 5.39981 22.935 5.8017 22.9684C6.19162 23.0008 6.66729 23 7.22 23V22ZM4 18.78H3C3 19.3327 2.9992 19.8084 3.03158 20.1983C3.06495 20.6002 3.13867 20.9997 3.33776 21.3829L4.22517 20.9219L5.11259 20.461C5.0865 20.4108 5.04763 20.3088 5.02472 20.0328C5.0008 19.7448 5 19.3663 5 18.78H4ZM5.07805 21.7748L5.53903 20.8874C5.35638 20.7925 5.20746 20.6436 5.11259 20.461L4.22517 20.9219L3.33776 21.3829C3.62239 21.9309 4.06915 22.3776 4.61708 22.6622L5.07805 21.7748ZM20 18.78H19C19 19.3663 18.9992 19.7448 18.9753 20.0328C18.9524 20.3088 18.9135 20.4108 18.8874 20.461L19.7748 20.9219L20.6622 21.3829C20.8613 20.9997 20.935 20.6002 20.9684 20.1983C21.0008 19.8084 21 19.3327 21 18.78H20ZM16.78 22V23C17.3327 23 17.8084 23.0008 18.1983 22.9684C18.6002 22.935 18.9997 22.8613 19.3829 22.6622L18.9219 21.7748L18.461 20.8874C18.4108 20.9135 18.3088 20.9524 18.0328 20.9753C17.7448 20.9992 17.3663 21 16.78 21V22ZM19.7748 20.9219L18.8874 20.461C18.7925 20.6436 18.6436 20.7925 18.461 20.8874L18.9219 21.7748L19.3829 22.6622C19.9309 22.3776 20.3776 21.9309 20.6622 21.3829L19.7748 20.9219ZM16.78 2V3C17.3663 3 17.7448 3.0008 18.0328 3.02472C18.3088 3.04763 18.4108 3.0865 18.461 3.11259L18.9219 2.22517L19.3829 1.33776C18.9997 1.13867 18.6002 1.06495 18.1983 1.03158C17.8084 0.999196 17.3327 1 16.78 1V2ZM20 5.22H21C21 4.66729 21.0008 4.19162 20.9684 3.8017C20.935 3.39982 20.8613 3.00034 20.6622 2.61708L19.7748 3.07805L18.8874 3.53903C18.9135 3.58924 18.9524 3.69125 18.9753 3.96722C18.9992 4.25517 19 4.63372 19 5.22H20ZM18.9219 2.22517L18.461 3.11259C18.6436 3.20746 18.7925 3.35638 18.8874 3.53903L19.7748 3.07805L20.6622 2.61708C20.3776 2.06915 19.9309 1.62239 19.3829 1.33776L18.9219 2.22517ZM7.22 2V1C6.66729 1 6.19162 0.999196 5.8017 1.03158C5.39981 1.06495 5.00034 1.13867 4.61708 1.33776L5.07805 2.22517L5.53903 3.11259C5.58924 3.0865 5.69125 3.04763 5.96722 3.02472C6.25517 3.0008 6.63372 3 7.22 3V2ZM4 5.22H5C5 4.63372 5.0008 4.25517 5.02472 3.96722C5.04763 3.69125 5.0865 3.58924 5.11259 3.53903L4.22517 3.07805L3.33776 2.61708C3.13867 3.00034 3.06495 3.39981 3.03158 3.8017C2.9992 4.19162 3 4.66729 3 5.22H4ZM5.07805 2.22517L4.61708 1.33776C4.06915 1.62239 3.62239 2.06915 3.33776 2.61708L4.22517 3.07805L5.11259 3.53903C5.20746 3.35638 5.35638 3.20746 5.53903 3.11259L5.07805 2.22517Z"
    fill="black"
  />
</svg>
</span>
{t('INSURANCE_LETTER_PDF_DESC')}
    
  </div>

  <div className="px-2">

    <button className="download-btn-pre-insurance  " onClick={handleDownloadPdf}>
           <span >
 <svg
  width={18}
  height={18}
  viewBox="0 0 18 18"
  fill="none"
  xmlns="http://www.w3.org/2000/svg"
>
  <path
    d="M10 2.25C10 1.69772 9.55228 1.25 9 1.25C8.44772 1.25 8 1.69772 8 2.25L9 2.25L10 2.25ZM8 10.5C8 11.0523 8.44772 11.5 9 11.5C9.55229 11.5 10 11.0523 10 10.5H9H8ZM12.7071 9.70711C13.0976 9.31658 13.0976 8.68342 12.7071 8.29289C12.3166 7.90237 11.6834 7.90237 11.2929 8.29289L12 9L12.7071 9.70711ZM9.53033 11.4697L8.82322 10.7626V10.7626L9.53033 11.4697ZM8.46967 11.4697L9.17678 10.7626H9.17678L8.46967 11.4697ZM6.70711 8.29289C6.31658 7.90237 5.68342 7.90237 5.29289 8.29289C4.90237 8.68342 4.90237 9.31658 5.29289 9.70711L6 9L6.70711 8.29289ZM3.25 12C3.25 11.4477 2.80228 11 2.25 11C1.69772 11 1.25 11.4477 1.25 12H2.25H3.25ZM16.75 12C16.75 11.4477 16.3023 11 15.75 11C15.1977 11 14.75 11.4477 14.75 12H15.75H16.75ZM14.5215 15.5048L14.0675 14.6138H14.0675L14.5215 15.5048ZM15.5048 14.5215L16.3958 14.9755V14.9755L15.5048 14.5215ZM2.49524 14.5215L1.60423 14.9755L2.49524 14.5215ZM3.47852 15.5048L3.02453 16.3958H3.02453L3.47852 15.5048ZM9 2.25L8 2.25L8 10.5H9H10L10 2.25L9 2.25ZM12 9L11.2929 8.29289L8.82322 10.7626L9.53033 11.4697L10.2374 12.1768L12.7071 9.70711L12 9ZM8.46967 11.4697L9.17678 10.7626L6.70711 8.29289L6 9L5.29289 9.70711L7.76256 12.1768L8.46967 11.4697ZM9.53033 11.4697L8.82322 10.7626C8.92085 10.6649 9.07915 10.6649 9.17678 10.7626L8.46967 11.4697L7.76256 12.1768C8.44598 12.8602 9.55402 12.8602 10.2374 12.1768L9.53033 11.4697ZM2.25 12H1.25V12.15H2.25H3.25V12H2.25ZM5.85 15.75V16.75H12.15V15.75V14.75H5.85V15.75ZM15.75 12.15H16.75V12H15.75H14.75V12.15H15.75ZM12.15 15.75V16.75C12.7636 16.75 13.2839 16.7508 13.7092 16.716C14.1463 16.6803 14.5704 16.6022 14.9755 16.3958L14.5215 15.5048L14.0675 14.6138C13.9913 14.6526 13.8597 14.6971 13.5464 14.7227C13.2213 14.7492 12.7966 14.75 12.15 14.75V15.75ZM15.75 12.15H14.75C14.75 12.7966 14.7492 13.2213 14.7227 13.5464C14.6971 13.8597 14.6526 13.9913 14.6138 14.0675L15.5048 14.5215L16.3958 14.9755C16.6022 14.5704 16.6803 14.1463 16.716 13.7092C16.7508 13.2839 16.75 12.7636 16.75 12.15H15.75ZM14.5215 15.5048L14.9755 16.3958C15.587 16.0842 16.0842 15.587 16.3958 14.9755L15.5048 14.5215L14.6138 14.0675C14.4939 14.3027 14.3027 14.4939 14.0675 14.6138L14.5215 15.5048ZM2.25 12.15H1.25C1.25 12.7636 1.24922 13.2839 1.28398 13.7092C1.31968 14.1463 1.39781 14.5704 1.60423 14.9755L2.49524 14.5215L3.38624 14.0675C3.34742 13.9913 3.30294 13.8597 3.27733 13.5464C3.25078 13.2213 3.25 12.7966 3.25 12.15H2.25ZM5.85 15.75V14.75C5.20344 14.75 4.77866 14.7492 4.45364 14.7227C4.14029 14.6971 4.00869 14.6526 3.93251 14.6138L3.47852 15.5048L3.02453 16.3958C3.42965 16.6022 3.85374 16.6803 4.29077 16.716C4.71613 16.7508 5.23644 16.75 5.85 16.75V15.75ZM2.49524 14.5215L1.60423 14.9755C1.91582 15.587 2.41301 16.0842 3.02453 16.3958L3.47852 15.5048L3.93251 14.6138C3.69731 14.4939 3.50608 14.3027 3.38624 14.0675L2.49524 14.5215Z"
    fill="#544196"
  />
</svg>

</span>
      <span className='px-2'>{t('INSURANCE_LETTER_PDF_DOWNLOAD')}</span>
          </button>
  </div>
</div>
            </div>
        <div style={{ display: 'flex', flexDirection: 'column', height: '80vh' }}>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <iframe
              src="https://api-prod.invozio.com/img/F%C3%B6rs%C3%A4kringsbrev.pdf"
              style={{ width: '100%', height: '100%', border: 'none' }}
              title="Insurance Letter PDF"
            />
          </div>
        
        </div>
      </GenericModal>
    </>
  );
};
